import { sendableAs, type ConnectedSender } from "@roof-hawk/core";

import { fetchGmailAccounts, type GmailAccountRow } from "../../lib/gmail";
import { useAsync } from "../../lib/hooks";
import { useSession } from "../../session";

/**
 * The signed-in person's own Gmail connection, or null.
 *
 * Every send button asks the same question — can this actually go, or is the
 * clipboard still the only way out — so it is asked in one place. The row is
 * readable by any member of staff (see the migration); the credential behind it
 * is not readable by anybody.
 */
export function useGmailAccount(): {
  account: GmailAccountRow | null;
  loading: boolean;
  /**
   * Why the answer is not known, when it is not.
   *
   * Returned because "not connected" and "we could not find out" look
   * identical to a send button and are not the same thing at all. Lysa found
   * Email it greyed out on the present screen with her Gmail connected and
   * sending fine (Jonathon, 2026-09-08); a screen that cannot tell those two
   * apart cannot say anything useful about either.
   */
  error: string | null;
  reload: () => Promise<void>;
} {
  const { profileId } = useSession();
  const query = useAsync(fetchGmailAccounts, []);
  const account =
    (query.data ?? []).find((row) => row.profile_id === profileId) ?? null;
  return {
    account,
    loading: query.loading,
    error: query.error,
    reload: async () => {
      await query.reload();
    },
  };
}

/**
 * The mailboxes the signed-in person may send from.
 *
 * Their own, always; everybody else's too if they are an admin. Jonathon,
 * 2026-08-24: "if I want to send an estimate for samuel i should be able to
 * select samuel as the sender."
 *
 * The list is a convenience, not the control. A browser can ask for any profile
 * it likes — the answer that counts is `resolveEmailSender` inside the Edge
 * Function, which is the only thing that can reach a refresh token. This is
 * here so the picker offers the same set the server will accept, and so it
 * offers nobody whose account is not connected: a name with no token behind it
 * is an offer of a failure.
 */
export function useGmailSenders(): {
  senders: ConnectedSender[];
  /** The signed-in person's own mailbox, or null if they have not connected. */
  own: ConnectedSender | null;
  loading: boolean;
  /** Why the list is empty, when it is empty because we could not read it. */
  error: string | null;
} {
  const { profileId, isAdmin, people } = useSession();
  const query = useAsync(fetchGmailAccounts, []);

  const nameOf = (id: string) =>
    people.find((person) => person.id === id)?.full_name ?? null;

  const connected: ConnectedSender[] = (query.data ?? []).map((row) => ({
    profileId: row.profile_id,
    emailAddress: row.email_address,
    name: nameOf(row.profile_id),
  }));

  return {
    senders: sendableAs(connected, {
      callerProfileId: profileId,
      callerIsAdmin: isAdmin,
    }),
    own: connected.find((row) => row.profileId === profileId) ?? null,
    loading: query.loading,
    error: query.error,
  };
}
